import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useReveal } from "@/hooks/use-reveal";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — why The Founders App exists" },
      {
        name: "description",
        content:
          "The Founders App is a private operating system for founders running more than one thing: one move a day, one risk a month, no audience and no guilt mechanics.",
      },
      { property: "og:title", content: "About — why The Founders App exists" },
      {
        property: "og:description",
        content: "A quiet operating system for operators. No audience, no streaks, no performance.",

      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/about" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Skylab Studio LLC",
          alternateName: "The Founders App",
          url: "/",
          address: {
            "@type": "PostalAddress",
            addressRegion: "CA",
            addressLocality: "San Francisco Bay Area",
            addressCountry: "US",
          },
        }),
      },
    ],
  }),
  component: About,
});

const PRINCIPLES = [
  {
    code: "01",
    title: "Private by default",
    body: "No feeds, no sharing, no leaderboard. The only person reading your record is you, and the app opens behind four digits.",
  },
  {
    code: "02",
    title: "One move beats ten lists",
    body: "A business moves when one real thing happens. We ask for that one thing, then ask what came of it. Everything else is noise dressed as productivity.",
  },
  {
    code: "03",
    title: "Never guilt",
    body: "No streak resets, no red warnings, no shame copy. Gaps are data. You read them, you adjust, you keep going.",
  },
  {
    code: "04",
    title: "Built for many businesses",
    body: "Operators rarely run one thing. Each business gets its own tasks, journal, phonebook and history — clean separation, one login.",

  },
];

function About() {
  useReveal();

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-5xl px-6 pt-16 pb-24 sm:px-10">
        <p className="reveal micro-label">About</p>
        <h1 className="reveal etched mt-5 max-w-3xl text-[2.5rem] leading-[0.95] font-semibold sm:text-6xl [--d:80ms]">
          Quiet tools for loud ambition.
        </h1>
        <p className="reveal mt-7 max-w-xl text-[0.95rem] leading-relaxed text-muted-foreground [--d:160ms]">
          The Founders App was built for the person running three things at once who never posts about any
          of them. It replaces the sprawling productivity stack with a single instrument: what will
          you move today, what did happen, and what are you willing to risk this month.
        </p>

        <div className="hairline scan-rule mt-14" />

        <div className="mt-14 grid gap-5 sm:grid-cols-2">
          {PRINCIPLES.map((p, i) => (
            <div
              key={p.code}
              className="reveal soft-panel p-7 transition-colors duration-300 hover:bg-accent/25"
              style={{ ["--d" as string]: `${140 + i * 90}ms` }}
            >
              <span className="text-[0.625rem] tracking-[0.2em] text-muted-foreground/50 tabular-nums">
                {p.code}
              </span>
              <h2 className="mt-4 text-lg font-medium">{p.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </div>
          ))}
        </div>

        <div className="reveal vault-panel mt-14 flex flex-wrap items-center justify-between gap-6 p-8 [--d:520ms]">
          <div>
            <p className="micro-label">Ready</p>
            <p className="mt-2 text-xl font-medium">Get on the early-access list.</p>

          </div>
          <Link
            to="/auth"
            className="sheen group inline-flex items-center gap-3 rounded-sm bg-primary px-7 py-4 text-sm font-semibold tracking-wide text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5"
          >
            Request access
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
